import { Controller } from '@hotwired/stimulus'

// I-UX-AUD-CHR-03 record tab bar (resources/_detail_tabs) wider than the page: the open tab is scrolled into view, and
// data-overflow-start / -end mark a side with hidden tabs (the CSS fades that edge).
// I-UXF-od-5: from lg the tabs that do not fit move into „Další ▾“ (item i = tab i), the open tab stays in the row.
// Re-laid out on resize of the bar or a tab, and after a Turbo morph (it restores the server's hidden attributes);
// data-tab-strip-ready ends the first-paint clip (CSS). Escape / a click outside close „Další“.
export default class TabStripController extends Controller<HTMLElement> {
  static override targets = ['strip', 'more', 'item']
  declare readonly stripTarget: HTMLElement
  declare readonly moreTarget: HTMLDetailsElement
  declare readonly itemTargets: HTMLElement[]
  private observer?: ResizeObserver
  private readonly wide = window.matchMedia('(min-width: 1024px)')
  private readonly relayout = (): void => { this.observe(); this.layout() }
  private readonly close = (event: Event): void => {
    if (!this.moreTarget.open) return
    if (event instanceof KeyboardEvent ? event.key === 'Escape' : !this.moreTarget.contains(event.target as Node)) this.moreTarget.open = false
  }

  override connect(): void {
    this.observe()
    this.layout()
    const current = this.stripTarget.querySelector<HTMLElement>('[aria-current="page"]')
    // scrollLeft, not scrollIntoView: that would also scroll the page under the sticky bar.
    if (current) this.stripTarget.scrollLeft = current.offsetLeft - (this.stripTarget.clientWidth - current.offsetWidth) / 2
    document.addEventListener('turbo:morph', this.relayout)
    document.addEventListener('click', this.close)
    document.addEventListener('keydown', this.close)
  }

  override disconnect(): void {
    this.observer?.disconnect()
    document.removeEventListener('turbo:morph', this.relayout)
    document.removeEventListener('click', this.close)
    document.removeEventListener('keydown', this.close)
  }

  update(): void {
    const { scrollLeft, scrollWidth, clientWidth } = this.stripTarget
    this.stripTarget.toggleAttribute('data-overflow-start', scrollLeft > 1)
    this.stripTarget.toggleAttribute('data-overflow-end', scrollLeft + clientWidth < scrollWidth - 1)
  }

  private observe(): void {
    this.observer?.disconnect()
    this.observer = new ResizeObserver(() => this.layout())
    this.observer.observe(this.element)
    this.tabs().forEach((tab) => this.observer?.observe(tab))
  }

  private tabs(): HTMLElement[] {
    return Array.from(this.stripTarget.querySelectorAll<HTMLElement>('[data-detail-tab]'))
  }

  private layout(): void {
    const tabs = this.tabs()
    tabs.forEach((tab) => { tab.hidden = false })
    this.itemTargets.forEach((item) => { item.hidden = true })
    this.moreTarget.hidden = true
    const overflows = (): boolean => this.stripTarget.scrollWidth > this.stripTarget.clientWidth + 1
    if (this.wide.matches && overflows()) {
      this.moreTarget.hidden = false
      for (let i = tabs.length - 1; i >= 0 && overflows(); i--) {
        const tab = tabs[i]
        if (!tab || tab.getAttribute('aria-current') === 'page') continue
        tab.hidden = true
        const item = this.itemTargets[i]
        if (item) item.hidden = false
      }
    }
    if (this.moreTarget.hidden) this.moreTarget.open = false
    this.element.setAttribute('data-tab-strip-ready', '')
    this.update()
  }
}
